import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ArrowLeft, BadgeCheck, MapPin, Eye, Heart, Star, ShieldCheck, Zap, Award, Play, Send, Handshake, Instagram, Youtube, Music2, Globe } from "lucide-react";
import api from "../lib/api";
import { fmt } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { LocationMap } from "../components/LocationMap";

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl border border-cardline bg-card2/60 p-3 text-center">
      <Icon className="mx-auto text-gold" size={18} />
      <div className="mt-1 font-heading text-lg font-extrabold">{value}</div>
      <div className="text-[11px] text-slate-400">{label}</div>
    </div>
  );
}

function SocialRow({ socials }) {
  const { t } = useI18n();
  if (!socials) return null;
  const links = [
    { url: socials.instagram, Icon: Instagram, key: "instagram" },
    { url: socials.tiktok, Icon: Music2, key: "tiktok" },
    { url: socials.youtube, Icon: Youtube, key: "youtube" },
    { url: socials.website, Icon: Globe, key: "website" },
  ].filter((l) => l.url);
  if (links.length === 0) return null;
  return (
    <div className="mt-3 flex justify-center gap-2" data-testid="creator-socials">
      {links.map(({ url, Icon, key }) => (
        <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`social-${key}`}
          className="grid h-9 w-9 place-items-center rounded-full border border-cardline bg-ink2/60 text-slate-300 transition-colors hover:border-gold hover:text-gold">
          <Icon size={16} />
        </a>
      ))}
    </div>
  );
}

export default function CreatorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [pFilter, setPFilter] = useState("all");

  useEffect(() => {
    api.get(`/creators/${id}`).then(({ data }) => setC(data.creator)).catch(() => toast.error("Introuvable"));
  }, [id]);

  if (!c) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;

  const aud = c.audience || {};
  const donut = [
    { name: t("women"), value: aud.women ?? 60, color: "#D9A441" },
    { name: t("men"), value: aud.men ?? 40, color: "#2f4d8a" },
  ];
  const ages = aud.ages || { "18-24": 32, "25-34": 41, "35-44": 18, "45+": 9 };
  const cities = aud.cities || [{ name: "Lyon", pct: 32 }, { name: "Paris", pct: 22 }];
  const ratingDist = [18, 7, 2, 1, 0];

  const portfolio = (c.portfolio || []).map((m) => (typeof m === "string" ? { type: "image", src: m } : m));
  const totalViews = portfolio.reduce((a, m, i) => a + (m.views || 8000 + i * 4200), 0);
  const pItems = portfolio.filter((m) => pFilter === "all" || m.type === pFilter);

  const message = () => navigate(`/messages?to=${c.id}`);
  const invite = () => toast.success(`${t("inviteCollab")} — ${c.name}`);

  return (
    <div className="animate-fade-in space-y-5">
      <div className="relative -mx-4 -mt-4">
        <img src={c.banner} alt="" className="h-36 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink" />
        <button onClick={() => navigate(-1)} data-testid="profile-back-btn"
          className="absolute left-4 top-4 grid h-9 w-9 place-items-center rounded-full glass text-white">
          <ArrowLeft size={18} />
        </button>
      </div>

      <div className="-mt-14 flex flex-col items-center px-4 text-center">
        <img src={c.avatar} alt={c.name} className="h-24 w-24 rounded-full border-4 border-ink object-cover" data-testid="profile-avatar" />
        <div className="mt-2 flex items-center gap-1.5">
          <h1 className="font-heading text-xl font-extrabold">{c.name}</h1>
          {c.verified && <span className="flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-xs font-semibold text-gold"><BadgeCheck size={13} /> {t("verified")}</span>}
        </div>
        {c.handle && <div className="mt-0.5 text-sm font-semibold text-gold">{c.handle}</div>}
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm text-slate-400">
          <span className="flex items-center gap-1"><MapPin size={13} /> {c.location}</span>
          {c.experience_level && <span className="flex items-center gap-1 rounded-full bg-ink2/70 px-2 py-0.5 text-xs text-slate-300"><Award size={12} className="text-gold" /> {c.experience_level}</span>}
        </div>
        <p className="mt-2 max-w-md text-sm text-slate-300">{c.bio}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-1.5">
          {c.categories?.map((k) => <span key={k} className="rounded-full bg-ink2/70 px-2.5 py-0.5 text-xs text-slate-300">{k}</span>)}
        </div>
        <SocialRow socials={c.socials} />
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Stat icon={Heart} value={fmt(c.followers)} label={t("followers")} />
        <Stat icon={Eye} value={fmt(c.avg_views)} label={t("avgViews")} />
        <Stat icon={Zap} value={`${c.engagement_rate}%`} label={t("engagement")} />
        <Stat icon={Award} value={c.posts} label={t("posts")} />
      </div>

      {/* Rating */}
      <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
        <h3 className="font-heading text-sm font-semibold text-slate-300">{t("avgRating")}</h3>
        <div className="mt-3 flex items-center gap-4">
          <div className="text-center">
            <div className="font-heading text-3xl font-extrabold text-gold">{c.rating}</div>
            <div className="text-xs text-slate-500">/ 5</div>
            <div className="mt-1 flex text-gold">{[0,1,2,3,4].map(i => <Star key={i} size={12} fill={i < Math.round(c.rating) ? "currentColor" : "none"} />)}</div>
            <div className="mt-1 text-[11px] text-slate-500">{t("basedOn")} {c.reviews} {t("reviews")}</div>
          </div>
          <div className="flex-1 space-y-1">
            {ratingDist.map((n, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-slate-400">{5 - i}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink2">
                  <div className="h-full bg-gold" style={{ width: `${(n / Math.max(...ratingDist, 1)) * 100}%` }} />
                </div>
                <span className="w-4 text-right text-slate-500">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="rounded-2xl border border-gold/30 bg-gold/5 p-4">
        <div className="flex items-center gap-2 font-heading text-sm font-semibold text-gold">
          <Award size={16} /> {t("trustedCreator")}
        </div>
        <p className="mt-1 text-xs text-slate-400">{t("approvedBy")}</p>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px]">
          {[[Zap, "Réactif"], [ShieldCheck, "Professionnel"], [Star, "Qualité"]].map(([Ic, l], i) => (
            <div key={i} className="flex flex-col items-center gap-1 rounded-xl bg-ink2/50 py-2">
              <Ic size={16} className="text-gold" /><span className="text-slate-300">{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Location + radius */}
      {c.lat != null && (
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4" data-testid="creator-location">
          <h3 className="mb-2 flex flex-wrap items-center gap-2 font-heading text-sm font-semibold">
            <MapPin size={16} className="text-gold" /> {t("locationTitle")}
            <span className="font-normal text-slate-400">· {t("radiusOfAction")}: {c.radius_km || 25} km</span>
          </h3>
          <LocationMap lat={c.lat} lng={c.lng} radiusKm={c.radius_km || 25} color="#3b82f6" height="13rem" />
        </div>
      )}

      {/* Audience */}
      <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
        <h3 className="font-heading text-sm font-semibold">{t("audience")}</h3>
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div className="relative">
            <ResponsiveContainer width="100%" height={120}>
              <PieChart>
                <Pie data={donut} dataKey="value" innerRadius={38} outerRadius={55} startAngle={90} endAngle={-270} paddingAngle={2}>
                  {donut.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-lg font-extrabold text-gold">{donut[0].value}%</span>
              <span className="text-[10px] text-slate-400">{t("women")}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold text-slate-400">{t("ageRange")}</div>
            {Object.entries(ages).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="w-12 text-slate-400">{k}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink2"><div className="h-full bg-gold" style={{ width: `${v}%` }} /></div>
                <span className="w-7 text-right text-slate-500">{v}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 border-t border-cardline pt-3">
          <div className="mb-1.5 text-[11px] font-semibold text-slate-400">{t("topCities")}</div>
          <div className="flex flex-wrap gap-2">
            {cities.map((ci) => (
              <span key={ci.name} className="rounded-full bg-ink2/70 px-2.5 py-1 text-xs text-slate-300">{ci.name} · {ci.pct}%</span>
            ))}
          </div>
        </div>
      </div>

      {/* Portfolio */}
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-sm font-semibold">{t("latestContent")}</h3>
          <span className="flex items-center gap-1 text-xs text-slate-400"><Eye size={12} className="text-gold" /> {fmt(totalViews)} {t("totalViews")}</span>
        </div>
        <div className="mb-2 flex gap-1.5" data-testid="portfolio-filters">
          {[["all", t("filterAll")], ["video", t("filterVideos")], ["image", t("filterPhotos")]].map(([k, l]) => (
            <button key={k} onClick={() => setPFilter(k)} data-testid={`portfolio-filter-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${pFilter === k ? "bg-gold text-ink" : "bg-card2/60 text-slate-300"}`}>{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {pItems.map((item, i) => (
            <div key={i} className="relative aspect-[3/4] overflow-hidden rounded-xl border border-cardline bg-ink2">
              {item.type === "video" ? (
                <video src={item.src} poster={item.poster} autoPlay muted loop playsInline preload="metadata"
                  className="h-full w-full object-cover" />
              ) : (
                <img src={item.src} alt="" className="h-full w-full object-cover" />
              )}
              {item.type === "video" && (
                <div className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full glass text-white">
                  <Play size={11} fill="currentColor" />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/95 via-ink/50 to-transparent p-2">
                {item.business && (
                  <div className="flex items-center gap-1 truncate text-[10px] text-slate-200">
                    <MapPin size={9} className="shrink-0 text-gold" /> {item.business}
                  </div>
                )}
                <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-white">
                  <Eye size={10} className="text-gold" /> {fmt(item.views || 8000 + i * 4200)} {t("views")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      {user?.role !== "creator" && (
        <div className="sticky bottom-24 flex gap-2">
          <button onClick={message} data-testid="profile-message-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-cardline bg-card2/80 py-3 text-sm font-semibold backdrop-blur">
            <Send size={16} /> {t("sendMessage")}
          </button>
          <button onClick={invite} data-testid="profile-invite-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-gold py-3 text-sm font-bold text-ink">
            <Handshake size={16} /> {t("inviteCollab")}
          </button>
        </div>
      )}
    </div>
  );
}
